'use client';

import type { ButtonClickEvent } from '@hc-frontend/core-ui';
import { Button, useEventBus, useTrackingContexts } from '@hc-frontend/core-ui';
import type {
  SendMetricEvent,
  SendMetricEventTarget,
} from '@hc-frontend/shells-shared';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { TypConnectMeHolder } from '../../molecules/typ-connect-me-holder/typ-connect-me-holder';
import type { ConnectMeButtonProps } from './connect-me-button.types';

export function ConnectMeButton({
  id = 'connect-me-button',
  metricName = 'connect-me',
  children,
  experience,
  callType,
  phoneNumber,
  destinationPhoneNumber,
  placement,
  sessionId,
  product,
  isSendingDisplayMetric = true,
  onClick,
  advertiser,
  trustedFormCertUrl,
  ...props
}: ConnectMeButtonProps) {
  const eventBus = useEventBus();
  const contexts = useTrackingContexts();

  const [buttonWasClicked, setButtonWasClicked] = useState(false);

  const clickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
      id,
      topic: 'metric.client.message',
      source: event.currentTarget,
      metric: {
        name: metricName,
        tags: {
          action: 'Click Call',
          experience,
          placement,
          product,
        },
      },
    });

    eventBus.publish<ButtonClickEvent>({
      id,
      topic: 'core.ui.button.click',
      source: event.currentTarget,
      contexts,
    });

    onClick?.(event);

    setButtonWasClicked(true);
  };

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (ref.current && isSendingDisplayMetric) {
      eventBus.publish<SendMetricEvent, SendMetricEventTarget>({
        id,
        topic: 'metric.client.message',
        source: ref.current,
        metric: {
          name: metricName,
          tags: {
            action: 'Display',
            experience,
            placement,
            product,
          },
        },
      });
    }
  }, [
    id,
    metricName,
    eventBus,
    experience,
    placement,
    product,
    isSendingDisplayMetric,
  ]);

  return buttonWasClicked ? (
    <TypConnectMeHolder
      experience={experience}
      phoneNumber={phoneNumber}
      destinationPhoneNumber={destinationPhoneNumber}
      placement={placement}
      sessionId={sessionId}
      product={product}
      advertiser={advertiser}
      trustedFormCertUrl={trustedFormCertUrl}
    />
  ) : (
    <Button
      id={id}
      ref={ref}
      onClick={clickHandler}
      data-call-call-type={callType}
      data-call-placement={placement}
      data-call-product={product}
      data-call-phone={phoneNumber}
      data-call-advertiser={advertiser}
      data-trusted-form-cert-url={trustedFormCertUrl}
      {...props}
    >
      {children}
    </Button>
  );
}
